import { useCallback, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import { defaultValueMapper, FormWizard, type FormWizardDrawerProps } from '../../../../../../components/form/drawer/wizard/form-wizard';
import { useFormWizardContextValue } from '../../../../../../components/form/drawer/wizard/form-wizard-context';
import type { FormWizardReviewStep } from '../../../../../../components/form/drawer/wizard/step/form-wizard-review-step';
import type { StepArray } from '../../../../../../components/form/drawer/wizard/step/form-wizard-step-builder';
import {
  ControlPersonForm,
  type ControlPersonFormValues,
  controlPersonFormSchema,
  mapControlPersonFormValuesToConfirmationDetails,
} from './control-person-form';

type FormSteps = { main: ControlPersonFormValues };

interface FormContext {
  programId: string | undefined;
}

const reviewStep: FormWizardReviewStep<FormSteps, FormContext> = {
  kind: 'review',
  buildConfirmationDetails: (values) => {
    return mapControlPersonFormValuesToConfirmationDetails(values.main);
  },
  saveCta: 'Update Control Person',
};

function MainForm({ lens }: { lens: Lens<ControlPersonFormValues> }) {
  const ctx = useFormWizardContextValue<FormSteps, FormContext>();
  return <ControlPersonForm lens={lens} programId={ctx?.programId} />;
}

const mainStep = {
  id: 'main',
  kind: 'form',
  schema: zodResolver(controlPersonFormSchema),
  children: MainForm,
} as const;

const initialSteps: StepArray<FormSteps, FormContext> = [mainStep] as const;

interface EditControlPersonDrawerProps extends FormWizardDrawerProps {
  handleSubmit: (person: ControlPersonFormValues) => void;
  initialValues: ControlPersonFormValues;
  programId: string | undefined;
}

export function EditControlPersonFormDrawer({
  handleSubmit,
  initialValues,
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  programId,
  ...rest
}: EditControlPersonDrawerProps) {
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const [buttonState, buttonRef] = useButtonState(false, undefined);
  const initialData = useMemo(() => ({ main: initialValues }), [initialValues]);
  const initialContext: FormContext = useMemo(() => ({ programId }), [programId]);

  const handleSave = useCallback(
    (values: FormSteps) => {
      handleSubmit(values.main);
    },
    [handleSubmit],
  );

  return (
    <FormWizard<FormSteps, FormSteps, FormContext>
      description="Form to edit a control person"
      handleMapFormData={defaultValueMapper}
      initialContext={initialContext}
      initialData={initialData}
      initialSteps={initialSteps}
      isOpen={isOpen}
      onComplete={handleSave}
      onOpenChange={onOpenChange}
      reviewStep={reviewStep}
      saveCtaRef={buttonRef}
      saveCtaState={buttonState}
      title="Edit Control Person"
      {...rest}
    />
  );
}
